/**
 * Lucide, the whole library.
 *
 * ISC licensed and free for commercial use, and already a production dependency on
 * collctiv.com — so generated pages using these cost no new assets.
 *
 * All 1,539 icons are available, loaded on demand. Importing the set eagerly would add well
 * over a megabyte to the bundle for a picker most people scroll past, so each icon is fetched
 * the first time it is used and cached from then on. A small common set is bundled eagerly so
 * the canvas has something to draw immediately rather than flashing empty on first paint.
 */

import { Suspense, lazy, useEffect, useState, type ReactNode } from 'react';
import dynamicIconImports from 'lucide-react/dynamicIconImports';
import { Gift, Users, Wallet, Link as LinkIcon, QrCode, ShieldCheck, Clock, PartyPopper, Cake, Plane, GraduationCap, HeartHandshake, Receipt, Bell, Check, Ticket, Baby, type LucideIcon } from 'lucide-react';

/** Every icon name Lucide ships, for the picker. */
export const ALL_ICON_NAMES: string[] = Object.keys(dynamicIconImports).sort();

/**
 * Bundled up front — the ones Collctiv's own pages reach for, so the canvas paints them
 * without a round trip.
 */
const EAGER: Record<string, LucideIcon> = {
  baby: Baby,
  bell: Bell,
  cake: Cake,
  check: Check,
  clock: Clock,
  gift: Gift,
  'graduation-cap': GraduationCap,
  'heart-handshake': HeartHandshake,
  link: LinkIcon,
  'party-popper': PartyPopper,
  plane: Plane,
  'qr-code': QrCode,
  receipt: Receipt,
  'shield-check': ShieldCheck,
  ticket: Ticket,
  users: Users,
  wallet: Wallet,
};

/** Suggested first, before the full alphabetical list. */
export const SUGGESTED_ICONS = Object.keys(EAGER);

/** Resolved lazily and kept, so an icon is fetched at most once per session. */
const cache = new Map<string, LucideIcon>();

const LazyIcon = ({ name, size }: { name: string; size: number }): React.ReactElement => {
  const Cmp = lazy(async () => {
    const loader = (dynamicIconImports as Record<string, () => Promise<{ default: LucideIcon }>>)[name];
    if (!loader) return { default: Gift };
    const mod = await loader();
    cache.set(name, mod.default);
    return { default: mod.default };
  });
  return <Cmp size={size} strokeWidth={2} aria-hidden />;
};

export function iconNode(name: string, size = 22): ReactNode {
  const Eager = EAGER[name] ?? cache.get(name);
  if (Eager) return <Eager size={size} strokeWidth={2} aria-hidden />;
  if (!(name in dynamicIconImports)) return <Gift size={size} strokeWidth={2} aria-hidden />;
  return (
    // A blank box of the right size while it loads, so nothing jumps.
    <Suspense fallback={<span style={{ display: 'inline-block', width: size, height: size }} />}>
      <LazyIcon name={name} size={size} />
    </Suspense>
  );
}

export function hasIcon(name: string): boolean {
  return name in dynamicIconImports;
}

/**
 * Search by name, with word-boundary matches first so "car" finds `car` before `scan-barcode`.
 */
export function searchIcons(query: string, limit = 120): string[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...SUGGESTED_ICONS, ...ALL_ICON_NAMES.filter((n) => !(n in EAGER))].slice(0, limit);

  const starts: string[] = [];
  const words: string[] = [];
  const rest: string[] = [];
  for (const name of ALL_ICON_NAMES) {
    if (name.startsWith(q)) starts.push(name);
    else if (name.split('-').some((w) => w.startsWith(q))) words.push(name);
    else if (name.includes(q)) rest.push(name);
    if (starts.length >= limit) break;
  }
  return [...starts, ...words, ...rest].slice(0, limit);
}

/** Preload an icon so a grid of results does not pop in one by one. */
export function usePreloadedIcons(names: string[]): number {
  const [, force] = useState(0);
  useEffect(() => {
    let cancelled = false;
    Promise.all(
      names
        .filter((n) => !cache.has(n) && !(n in EAGER) && n in dynamicIconImports)
        .slice(0, 60)
        .map(async (n) => {
          const loader = (dynamicIconImports as Record<string, () => Promise<{ default: LucideIcon }>>)[n];
          const mod = await loader!();
          cache.set(n, mod.default);
        }),
    ).then(() => {
      if (!cancelled) force((v) => v + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [names.join(',')]);
  return cache.size;
}
